/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch'; // TODO: Dynamically import this with feature detection
import 'intersection-observer'; // TODO: Dynamically import this with feature detection
import React from 'react';
import ReactDOM from 'react-dom';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import smoothscroll from 'smoothscroll-polyfill';
import * as Sentry from '@sentry/react';
import App from './components/App';
import createFetch from './createFetch';
import configureStore from './store/configureStore';
import { updateMeta, updateLink } from './DOMUtils';
import history from './history';
import createApolloClient from './core/createApolloClient';
import createAlgoliaClient from './helpers/searchClient';
import { setCanonicalUrl, getHrefLangs } from './helpers/url';
import router from './router';
import { getIntl } from './actions/intl';
import { IS_EXTERNAL, PAGE_LOAD, SALES_CHANNELS } from './constants';

// Universal HTTP client
const fetch = createFetch(window.fetch, {
  baseUrl: window.App.apiUrl,
});

const {
  graphqlHost,
  cmsHost,
  authHost,
  selfDomain,
  selfHost,
  clientSecret,
  reCaptcha,
  paymentMethods,
  algolia: { applicationId, apiKey, prefix },
  canonicalUrl,
  sentryDsn,
  sentryEnvironment,
  version,
} = window.App.config;

Sentry.init({
  dsn: sentryDsn,
  release: `react-salesfront-mainsite@${version}`,
  environment: `${sentryEnvironment}-browser`,
});

const apolloClient = createApolloClient({ graphqlHost, cmsHost });
const algoliaClient = createAlgoliaClient({ apiKey, applicationId, prefix });

// Initialize a new Redux store
// http://redux.js.org/docs/basics/UsageWithReact.html
const store = configureStore(window.App.state, {
  apolloClient,
  fetch,
  history,
  config: window.App.config,
});

// Enables critical path CSS rendering
// https://github.com/kriasoft/isomorphic-style-loader
const insertCss = (...styles) => {
  // eslint-disable-next-line no-underscore-dangle
  const removeCss = styles.map(x => x._insertCss());
  return () => {
    removeCss.forEach(f => f());
  };
};

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  config: {
    graphqlHost,
    cmsHost,
    authHost,
    clientSecret,
    reCaptcha,
    paymentMethods,
    sentryDsn,
    sentryEnvironment,
    selfDomain,
    selfHost,
    algolia: { applicationId, apiKey, prefix },
    canonicalUrl,
    version,
  },
  algoliaClient,
  // For react-apollo
  client: apolloClient,
  store,
  storeSubscription: null,
  // Universal HTTP client
  fetch,
  // intl instance as it can be get with injectIntl
  intl: store.dispatch(getIntl()),
};

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance;

// initiating scroll polyfill for smooth scroll support in all browsers
// https://www.npmjs.com/package/smoothscroll-polyfill
smoothscroll.polyfill();

const scrollPositionsHistory = {};

// Re-render the app when window.location changes
// This seems to be called on initial page render so routing is always done twice on the client?
async function onLocationChange({ location, action }) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.pathname] = {
    rootScrollTop: document.getElementById('root')?.scrollTop || 0,
    windowScrollTop: window.scrollY || 0, // on mobile we have scroll on documentElement not on root so we will use both
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH' && scrollPositionsHistory[location.pathname]) {
    delete scrollPositionsHistory[location.pathname];
  }

  currentLocation = location;

  const isInitialRender = !action;
  try {
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);
    context.locale = store.getState().intl.locale;
    context.intl = store.dispatch(getIntl());

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);
    context.addCompanyMarkup = route?.addCompanyMarkup;
    context.addProductMarkup = route?.addProductMarkup;
    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      console.error('Prevent multiple page renders during the routing process');
      return;
    }

    if (route.redirect) {
      if (IS_EXTERNAL.test(route.redirect)) {
        window.location = route.redirect;
        return;
      }
      history.replace(
        route.redirect + (route.from ? `?from=${route.from}` : ''),
      );
      return;
    }
    // for forceRender do not hydrate html on client side, instead overwrite with new client render
    // for now this is a fix for getting mixed up html after channel changes on client side
    const hydrateHtml = isInitialRender && !route.forceRender;
    const renderReactApp = hydrateHtml ? ReactDOM.hydrate : ReactDOM.render;
    appInstance = renderReactApp(
      <App context={context} insertCss={insertCss}>
        {route.component}
      </App>,
      container,
      () => {
        if (isInitialRender) {
          // Switch off the native scroll restoration behavior and handle it manually
          // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
          if (window.history && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }

          const elem = document.getElementById('css');
          if (elem) elem.parentNode.removeChild(elem);
          return;
        }

        document.title = route.title;

        const getLangHrefs = window => {
          const {
            intl: { locale, countryCode },
            config: { salesChannels },
          } = store.getState();

          const {
            location: { href },
          } = window;

          const { salesFlows = {} } = salesChannels[SALES_CHANNELS.B2C];
          // products only need alternate links for countries that they are available in
          const alternateLinkSalesFlows = route?.alternateCountries
            ? Object.keys(salesFlows)?.filter(({ countryCode: cc }) =>
                route.alternateCountries.includes(cc),
              ) || []
            : salesFlows;

          return getHrefLangs({
            salesFlows: Object.keys(alternateLinkSalesFlows).map(country => {
              return {
                countryCode: country,
                supportedLanguages: salesFlows[country].supportedLanguages.map(
                  ({ label }) => label,
                ),
              };
            }),
            href,
            locale,
            countryCode,
          });
        };

        updateMeta('description', route.description || route.title);
        // Update necessary tags in <head> at runtime here, ie:
        // updateMeta('keywords', route.keywords);
        // updateCustomMeta('og:url', route.canonicalUrl);
        // updateCustomMeta('og:image', route.imageUrl);
        // updateLink('canonical', getCanonicalUrl(window));
        updateLink('alternate', getLangHrefs(window));
        setCanonicalUrl(window);
        // etc.

        // Restore the scroll position if it was saved into the state
        // or scroll to the given #hash anchor
        // or scroll to top of the page
        const targetHash = location.hash ?? location.hash.substr(1);
        const target = targetHash ?? document.getElementById(targetHash);

        const newScrollPosition = target
          ? window.scrollY + target.getBoundingClientRect().top
          : scrollPositionsHistory[location.pathname]?.rootScrollTop || 0;

        document.getElementById('root')?.scrollTo({ top: newScrollPosition });
        window.scrollTo({
          top: scrollPositionsHistory[location.pathname]?.windowScrollTop || 0,
        });
      },
    );
    store.dispatch({
      type: PAGE_LOAD,
      payload: {
        redirecting: false,
        pathname: context.pathname,
      },
    });
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error');
      window.location.reload();
    }
  }
}

let isHistoryObserved = false;
export default function main() {
  // Handle client-side navigation by using HTML5 History API
  // For more information visit https://github.com/mjackson/history#readme
  currentLocation = history.location;
  if (!isHistoryObserved) {
    isHistoryObserved = true;
    history.listen(onLocationChange);
  }
  onLocationChange({ location: currentLocation });
}

// globally accessible entry point
window.RSK_ENTRY = main;

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange({ location: currentLocation });
  });
}

// Disable React Intl error messages

// eslint-disable-next-line
const consoleError = console.error.bind(console);
// eslint-disable-next-line
console.error = (message, ...args) => {
  if (
    typeof message === 'string' &&
    message.startsWith('[React Intl] Missing message:')
  ) {
    return;
  }
  consoleError(message, ...args);
};
